import { AutocompleteChangeReason, AutocompleteRenderOptionState, Box, Stack, Theme } from '@mui/material';
import {
    ChipAutocomplete,
    PoolButton,
    PoolModal,
    PoolTextArea,
    PoolTypography,
    ScrollToFieldError,
    SectionDivider,
} from '@synergy/components';
import { Classification, Tag } from '@synergy/models';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaloFormSection, PaloLinkRow } from '@synergy/components-palo';
import { SubPageHandle, fields, useFormikSubPage } from '@synergy/utils';
import { SyntheticEvent, forwardRef, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { useLocation, useNavigate } from 'react-router';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import * as CustomerDetailOverviewActions from './store/customer-detail-overview-actions';
import * as TagsActions from '../../../../../common/actions/tag-actions';
import { ClassificationAutocomplete } from '../../../../../common/components/classification-autocomplete/classification-autocomplete';
import { CustomerDetailOverviewBankAccounts } from './components/customer-detail-overview-bank-accounts';
import { CustomerDetailOverviewBasicInfos } from './components/customer-detail-overview-basic-infos';
import { CustomerDetailOverviewBlacklist } from './components/customer-detail-overview-blacklist';
import { CustomerDetailOverviewCategories } from './components/customer-detail-overview-categories';
import { CustomerDetailOverviewFormValues } from './models/customer-detail-overview-form-values';
import { CustomerDetailOverviewNotifications } from './components/customer-detail-overview-notifications';
import { EmailTable } from '../../../components/emails/emails-table';
import { InternalNotes } from '../../../../../common/components/internal-notes/internal-notes';
import { NoteType } from '../../../../../common/models/enums/note-type';
import { PaloAccountHead } from '../../../components/palo-account-head';
import { PaloDirtyComparison } from '../../../../../common/components/palo/palo-dirty-context';
import { PaloSectionText } from '../../../../../common/components/palo/palo-section-text';
import { ParentCustomerAutocompleteList } from './components/parent-customer-autocomplete-list';
import { PhonesTable } from '../../../components/phones/phones-table';
import {
    customerDetailOverviewBaselineSelector,
    customerDetailOverviewFormDataSelector,
    customerSelector,
} from './store/customer-detail-overview-selectors';
import { customerDetailOverviewValidationSchema } from './customer-detail-overview-validation-schema';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    // No gap of its own: every child is a PaloFormSection and brings the 20
    // pixels the rest of the app puts between sections. With both, the mask
    // ran 68 pixels of nothing between one heading and the next.
    container: {
        display: 'flex',
        flexDirection: 'column',
    },
    highlight: {
        color: theme.palette.primary.main,
    },
}));

/** The addresses are compared on their own tab (#2261). */
const withoutAddresses = (values?: CustomerDetailOverviewFormValues) => {
    if (!values) {
        return undefined;
    }

    const { addresses: _, ...rest } = values;

    return rest;
};

export const CustomerDetailOverview = forwardRef<SubPageHandle, Record<string, unknown>>((_, ref) => {
    const classes = useStyles();
    const dispatch = useThunkDispatch();
    const intl = useIntl();

    const tags = useAppSelector(state => state.tag.tags);
    const customer = useAppSelector(customerSelector);
    const formikRef = useRef<FormikProps<CustomerDetailOverviewFormValues>>(null);
    const { formValues, errors, touched } = useAppSelector(customerDetailOverviewFormDataSelector);
    // The customer as the server has it — see PaloDirtyComparison.
    const baseline = useAppSelector(customerDetailOverviewBaselineSelector);
    const [showTagsDeleteModal, setShowTagsDeleteModal] = useState(false);
    const [showClassificationDeleteModal, setShowClassificationDeleteModal] = useState(false);
    const fieldNames = fields<CustomerDetailOverviewFormValues>();
    const navigate = useNavigate();
    const location = useLocation();
    // The addresses have a tab of their own beside this one (#2261).
    const showAddresses = () => navigate(location.pathname.replace(/[^/]+$/, 'addresses'));

    useFormikSubPage(ref, formikRef, (values, isDirty, isValid, errs) => {
        dispatch(CustomerDetailOverviewActions.updateCustomerDetail(values, isDirty, isValid, errs));
    });

    const handleTagsChange =
        (formikProps: FormikProps<CustomerDetailOverviewFormValues>) =>
        (_event: SyntheticEvent<Element, Event>, values: Tag[], reason: AutocompleteChangeReason) => {
            if (reason === 'clear') {
                setShowTagsDeleteModal(true);
            } else {
                formikProps.setFieldValue(fieldNames.tags, values);
            }
        };

    const handleTagsDelete = (formikProps: FormikProps<CustomerDetailOverviewFormValues>) => {
        formikProps.setFieldValue(fieldNames.tags, []);
        setShowTagsDeleteModal(false);
    };

    const handleClassificationChange =
        (formikProps: FormikProps<CustomerDetailOverviewFormValues>) =>
        (_event: SyntheticEvent, values: Classification[], reason: AutocompleteChangeReason) => {
            if (reason === 'clear') {
                setShowClassificationDeleteModal(true);
            } else {
                formikProps.setFieldValue(fieldNames.classifications, values);
            }
        };

    const handleClassificationsDelete = (formikProps: FormikProps<CustomerDetailOverviewFormValues>) => {
        formikProps.setFieldValue(fieldNames.classifications, []);
        setShowClassificationDeleteModal(false);
    };

    const getTagsDeleteModalActionChildren = (formikProps: FormikProps<CustomerDetailOverviewFormValues>) => {
        return [
            <PoolButton key="2" variant="text" styleType="firstLevel" onClick={() => setShowTagsDeleteModal(false)}>
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>,
            <PoolButton key="1" styleType="firstLevel" color="secondary" onClick={() => handleTagsDelete(formikProps)}>
                <FormattedMessage id="app.common.delete" />
            </PoolButton>,
        ];
    };

    const getClassificationsModalActionChildren = (formikProps: FormikProps<CustomerDetailOverviewFormValues>) => {
        return [
            <PoolButton
                key="2"
                variant="text"
                styleType="firstLevel"
                onClick={() => setShowClassificationDeleteModal(false)}
            >
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>,
            <PoolButton
                key="1"
                styleType="firstLevel"
                color="secondary"
                onClick={() => handleClassificationsDelete(formikProps)}
            >
                <FormattedMessage id="app.common.delete" />
            </PoolButton>,
        ];
    };

    const renderOption = (item: Tag, state: AutocompleteRenderOptionState) => {
        const nameMatches = match(item.name, state.inputValue);
        const nameParts = parse(item.name, nameMatches);

        return (
            <div>
                {nameParts.map((namePart, index) => (
                    <span key={index} className={namePart.highlight ? classes.highlight : ''}>
                        {namePart.text}
                    </span>
                ))}
            </div>
        );
    };

    const handleTagsOpen = () => {
        if (tags.length === 0) {
            dispatch(TagsActions.fetchTags());
        }
    };

    return (
        <Formik
            onSubmit={() => Promise.resolve()}
            initialValues={formValues}
            validationSchema={customerDetailOverviewValidationSchema}
            enableReinitialize={true}
            initialErrors={errors}
            initialTouched={touched}
            innerRef={formikRef}
        >
            {formikProps => (
                <div className={classes.container}>
                    {/* Against the server's copy, not Formik's own initial values:
                        leaving a tab parks the input in the store and Formik would
                        take that for a save (#2159). */}
                    <PaloDirtyComparison<CustomerDetailOverviewFormValues>
                        baseline={withoutAddresses(baseline)}
                        normalize={withoutAddresses}
                    />
                    <CustomerDetailOverviewBasicInfos>
                        <PaloAccountHead
                            onShowAddresses={showAddresses}
                            responsibleAllWhenEmpty={true}
                            parentField={
                                <Field name={fieldNames.parent}>
                                    {({ field, form }: FieldProps<{ id: number; name: string }>) => (
                                        <ParentCustomerAutocompleteList
                                            label={<></>}
                                            required={false}
                                            onSelected={selected => form.setFieldValue(field.name, selected)}
                                            customerId={customer?.id}
                                            {...field}
                                        />
                                    )}
                                </Field>
                            }
                        />
                    </CustomerDetailOverviewBasicInfos>

                    <ScrollToFieldError />
                    {/* Two equal columns under the head, as in the template of
                        #2261: a wide table inside must not squeeze its neighbour. */}
                    <Box sx={{ containerType: 'inline-size' }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 1fr)',
                                columnGap: '20px',
                                // Side by side only where both tables fit: below,
                                // the bank data scrolled sideways (R-205).
                                '@container (min-width: 1100px)': {
                                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                                },
                            }}
                        >
                            <PaloFormSection title={<FormattedMessage id="app.common.bankData" />}>
                                <CustomerDetailOverviewBankAccounts />
                            </PaloFormSection>
                            <PaloFormSection
                                title={<FormattedMessage id="app.contactsAndOrganizations.detail.notifications" />}
                            >
                                <Stack gap={1.5}>
                                    <PaloSectionText id="app.contactsAndOrganizations.detail.notifications.text" />
                                    <CustomerDetailOverviewNotifications />
                                </Stack>
                            </PaloFormSection>
                            <PaloFormSection
                                title={
                                    <FormattedMessage id="app.contactsAndOrganizations.detail.classificationAndTags" />
                                }
                            >
                                <Stack gap={1.5}>
                                    <Field name={fieldNames.classifications}>
                                        {({ field }: FieldProps<Classification[]>) => (
                                            <ClassificationAutocomplete
                                                value={field.value}
                                                onChange={handleClassificationChange(formikProps)}
                                            />
                                        )}
                                    </Field>
                                    <Field name={fieldNames.tags}>
                                        {({ field }: FieldProps<Tag[]>) => (
                                            <ChipAutocomplete
                                                selectAll={true}
                                                noOptionsText={<FormattedMessage id="app.common.emptyResult" />}
                                                options={tags}
                                                getOptionLabel={tag => tag.name}
                                                renderOption={renderOption}
                                                getChipLabel={tag => tag.name}
                                                onChange={handleTagsChange(formikProps)}
                                                value={field.value}
                                                textfieldPlaceholder="app.common.addTag"
                                                onOpen={handleTagsOpen}
                                            />
                                        )}
                                    </Field>
                                </Stack>
                            </PaloFormSection>
                            <PaloFormSection
                                title={
                                    <FormattedMessage id="app.contactsAndOrganizations.detail.internalNotes.title" />
                                }
                            >
                                <Stack gap={1.5}>
                                    <PaloSectionText id="app.contactsAndOrganizations.detail.internalNotes.text" />
                                    {customer?.id && (
                                        <InternalNotes noteType={NoteType.Customers} entityId={customer.id} />
                                    )}
                                </Stack>
                            </PaloFormSection>
                        </Box>
                    </Box>

                    <PaloFormSection
                        title={<FormattedMessage id="app.contactsAndOrganizations.detail.internalNotes" />}
                        collapsible={true}
                    >
                        <Field name={fieldNames.internalNotes}>
                            {({ field }: FieldProps<string>) => (
                                <>
                                    <PoolTextArea
                                        placeholder={intl.formatMessage({ id: 'app.label.description' })}
                                        rowsMax="50"
                                        rows="3"
                                        fullWidth={true}
                                        {...field}
                                    />
                                    <PaloLinkRow text={field.value} />
                                </>
                            )}
                        </Field>
                    </PaloFormSection>

                    {/* The standard mail and phone stand in the head; further
                        ones are kept here (#2326 R-245). */}
                    <PaloFormSection
                        title={<FormattedMessage id="app.contactsAndOrganizations.detail.allEmailsAndPhones" />}
                        collapsible={true}
                        defaultOpen={false}
                    >
                        <EmailTable withMain={false} />
                        <SectionDivider />
                        <PhonesTable withMain={false} />
                    </PaloFormSection>

                    <PaloFormSection title={<FormattedMessage id="app.common.suppliers" />}>
                        <CustomerDetailOverviewBlacklist formikProps={formikProps} />
                    </PaloFormSection>

                    <PaloFormSection title={<FormattedMessage id="app.label.categories" />} collapsible={true}>
                        <CustomerDetailOverviewCategories />
                    </PaloFormSection>

                    <PoolModal
                        title={<FormattedMessage id="app.contactsAndOrganizations.detail.deleteTags" />}
                        handleClose={() => setShowTagsDeleteModal(false)}
                        isVisible={showTagsDeleteModal}
                        actionChildren={getTagsDeleteModalActionChildren(formikProps)}
                        hasModalTypography={true}
                    >
                        <PoolTypography id="app.contactsAndOrganizations.detail.deleteTagsMessage" />
                    </PoolModal>

                    <PoolModal
                        title={<FormattedMessage id="app.contactsAndOrganizations.detail.deleteClassification" />}
                        handleClose={() => setShowClassificationDeleteModal(false)}
                        isVisible={showClassificationDeleteModal}
                        actionChildren={getClassificationsModalActionChildren(formikProps)}
                        hasModalTypography={true}
                    >
                        <PoolTypography id="app.contactsAndOrganizations.detail.deleteClassificationMessage" />
                    </PoolModal>
                </div>
            )}
        </Formik>
    );
});
